import { useCallback, useEffect, useState } from 'react'
import { ExternalLink, GitBranch, Plus, Rocket, Trash2 } from 'lucide-react'
import type { ProjectDto } from '@doable/shared-types'
import { api, type ApiError, type ProjectWithDraft } from '../lib/api'
import { useAuthStore } from '../lib/auth-store'
import { navigate } from '../lib/router'
import { NewProjectModal } from '../components/NewProjectModal'
import { ImportFromGitlabModal } from '../components/ImportFromGitlabModal'
import { BuildLogModal } from '../components/BuildLogModal'
import { AppShell } from '../components/shell/AppShell'
import { PageHeader } from '../components/shell/PageHeader'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Badge, StatusBox, type BadgeVariant } from '../components/ui/badge'
import { EmptyState } from '../components/ui/empty-state'
import { Dialog } from '../components/ui/dialog'

type Me = {
  user: { id: string; email: string; name: string | null; emailVerified: boolean }
  tenant: { id: string; slug: string; name: string; role: string } | null
}

const STATUS_TONE: Record<string, BadgeVariant> = {
  draft: 'outline',
  building: 'volt',
  ready: 'success',
  live: 'success',
  failed: 'destructive',
  archived: 'outline',
}

export function DashboardPage() {
  const accessToken = useAuthStore((s) => s.accessToken)
  const clear = useAuthStore((s) => s.clear)
  const [me, setMe] = useState<Me | null>(null)
  const [projects, setProjects] = useState<ProjectDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showNewProject, setShowNewProject] = useState(false)
  const [showImportGitlab, setShowImportGitlab] = useState(false)
  const [hasGitlab, setHasGitlab] = useState<boolean | null>(null)
  const [hasApiKey, setHasApiKey] = useState<boolean | null>(null)
  const [buildingProject, setBuildingProject] = useState<ProjectDto | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  function patchProject(id: string, patch: Partial<ProjectDto>) {
    setProjects((prev) =>
      prev ? prev.map((p) => (p.id === id ? { ...p, ...patch } : p)) : prev,
    )
  }

  async function doDelete(id: string) {
    if (!accessToken) return
    setDeletingId(id)
    try {
      await api.deleteProject(accessToken, id)
      setProjects((prev) => (prev ? prev.filter((p) => p.id !== id) : prev))
    } catch (err) {
      setError((err as ApiError).title ?? 'Delete failed')
    } finally {
      setDeletingId(null)
      setConfirmDeleteId(null)
    }
  }

  const handleAuthError = useCallback(
    (err: ApiError) => {
      if (err.status === 401) {
        clear()
        navigate('/login')
        return true
      }
      return false
    },
    [clear],
  )

  useEffect(() => {
    if (!accessToken) {
      navigate('/login')
      return
    }
    Promise.all([
      api.me(accessToken),
      api.listProjects(accessToken),
      api.getWorkspaceSettings(accessToken),
    ])
      .then(([meData, projectsData, settings]) => {
        setMe(meData)
        setProjects(projectsData.projects)
        setHasApiKey(settings.hasApiKey)
        setHasGitlab(settings.hasGitlabToken)
      })
      .catch((err: ApiError) => {
        if (handleAuthError(err)) return
        setError(err.title ?? 'Failed to load dashboard')
      })
  }, [accessToken, handleAuthError])

  if (!accessToken) return null

  // Setup checklist — only renders while there's still something to do.
  // Steps disappear as the user completes them. We don't gate project
  // creation on any of these (a tenant on managed credits has no Anthropic
  // key by design); each step is "do this for the full experience".
  const steps: Array<{ done: boolean; label: string; cta: string; href: string }> = []
  if (hasApiKey === false) {
    steps.push({ done: false, label: 'Connect your Anthropic key (or use free credits)', cta: 'Connect', href: '/settings' })
  }
  if (hasGitlab === false) {
    steps.push({ done: false, label: 'Connect GitLab so projects auto-create a repo', cta: 'Connect', href: '/settings' })
  }

  return (
    <AppShell crumbs={[{ label: 'Projects' }]}>
      <PageHeader
        eyebrow={me?.tenant?.name ?? 'Workspace'}
        title={
          <>
            Projects
            {me && !me.user.emailVerified ? <Badge variant="warning">Verify email</Badge> : null}
          </>
        }
        description="Describe what you want, let Claude build it, and ship it to a live URL."
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowImportGitlab(true)}
              disabled={!hasGitlab}
              title={hasGitlab ? 'Import a repo from your GitLab account' : 'Connect GitLab in Settings to enable'}
            >
              <GitBranch /> Import from GitLab
            </Button>
            <Button size="sm" onClick={() => setShowNewProject(true)}>
              <Plus /> New project
            </Button>
          </>
        }
      />

      {steps.length > 0 ? (
        <Card>
          <CardHeader className="flex-row items-baseline justify-between">
            <CardTitle>Get set up</CardTitle>
            <span className="text-xs text-muted-foreground">
              {steps.length} step{steps.length === 1 ? '' : 's'} left
            </span>
          </CardHeader>
          <CardContent>
            <ol className="divide-y">
              {steps.map((s, i) => (
                <li key={s.label} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3">
                    <span
                      aria-hidden
                      className={
                        'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums ' +
                        (s.done ? 'bg-success-soft text-success-ink' : 'bg-volt-soft text-volt-ink')
                      }
                    >
                      {s.done ? '✓' : i + 1}
                    </span>
                    <span className={s.done ? 'text-success-ink' : 'text-foreground'}>{s.label}</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => navigate(s.href)}>
                    {s.cta}
                  </Button>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      ) : null}

      {error ? <StatusBox tone="destructive">{error}</StatusBox> : null}

      {projects === null ? (
        <Card>
          <CardContent className="space-y-3">
            <div className="dwlt-skeleton h-12" />
            <div className="dwlt-skeleton h-12" />
            <div className="dwlt-skeleton h-12" />
          </CardContent>
        </Card>
      ) : projects.length === 0 ? (
        <EmptyState
          icon={<Rocket />}
          title="Build your first website with AI"
          body="Describe what you want — a landing page, a portfolio, a booking site — and Claude writes the code, deploys it, and gives you a live URL. About 90 seconds."
          action={
            <>
              <Button onClick={() => setShowNewProject(true)}>
                <Plus /> Create your first project
              </Button>
              {hasGitlab ? (
                <Button variant="ghost" onClick={() => setShowImportGitlab(true)}>
                  <GitBranch /> Import an existing GitLab repo
                </Button>
              ) : null}
            </>
          }
        />
      ) : (
        <Card className="py-0 gap-0">
          <ul className="divide-y">
            {projects.map((p) => {
              const isBuilding = p.status === 'building'
              const isDeleting = deletingId === p.id
              return (
                <li key={p.id} className="flex items-center gap-4 px-4 py-3 transition-colors hover:bg-muted/50">
                  <button
                    type="button"
                    onClick={() => navigate(`/projects/${p.id}`)}
                    className="flex min-w-0 flex-1 flex-col items-start text-left"
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <p className="truncate text-sm font-medium text-foreground">{p.name}</p>
                      <Badge variant={STATUS_TONE[p.status] ?? 'outline'}>{p.status}</Badge>
                      {(p as ProjectWithDraft).hasUnpublishedChanges ? (
                        <Badge variant="warning" title="Changes on the draft preview that are not published yet">Draft</Badge>
                      ) : null}
                    </div>
                    {p.description ? (
                      <p className="mt-0.5 max-w-xl truncate text-sm text-muted-foreground">{p.description}</p>
                    ) : null}
                    <p className="mt-1 text-xs text-muted-foreground">
                      <span className="font-mono">{p.stack}</span>
                      <span className="mx-1.5">·</span>
                      <span className="font-mono">/{p.slug}</span>
                      <span className="mx-1.5">·</span>
                      {new Date(p.createdAt).toLocaleDateString()}
                    </p>
                  </button>
                  <div className="flex shrink-0 items-center gap-2">
                    {(() => {
                      // Live URL when published; otherwise the draft preview, marked as such.
                      const draft = (p as ProjectWithDraft).draftUrl ?? null
                      const href = p.previewUrl ?? draft
                      if (!href) return null
                      const isDraftOnly = !p.previewUrl && !!draft
                      return (
                        <a
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title={isDraftOnly ? 'Draft preview (not published)' : 'Live site'}
                          className="lp-arrow-link hidden max-w-56 items-center gap-1 truncate text-xs font-medium text-volt-ink hover:underline md:inline-flex"
                        >
                          {isDraftOnly ? <Badge variant="warning" className="h-4 px-1.5 text-[10px]">Draft</Badge> : null}
                          <span className="truncate">{href.replace(/^https?:\/\//, '')}</span>
                          <ExternalLink className="size-3 shrink-0" />
                        </a>
                      )
                    })()}
                    <Button
                      size="sm"
                      variant={p.status === 'live' ? 'outline' : 'default'}
                      disabled={isBuilding || isDeleting}
                      onClick={(e) => {
                        e.stopPropagation()
                        patchProject(p.id, { status: 'building' })
                        setBuildingProject(p)
                      }}
                    >
                      {isBuilding ? 'Building…' : p.status === 'live' ? 'Rebuild' : 'Build'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      disabled={isBuilding || isDeleting}
                      onClick={(e) => {
                        e.stopPropagation()
                        setConfirmDeleteId(p.id)
                      }}
                      title="Delete project"
                      aria-label="Delete project"
                      className="text-muted-foreground hover:text-destructive"
                    >
                      {isDeleting ? <span className="text-xs">…</span> : <Trash2 />}
                    </Button>
                  </div>
                </li>
              )
            })}
          </ul>
        </Card>
      )}

      {showNewProject && (
        <NewProjectModal
          onClose={() => setShowNewProject(false)}
          onCreated={(project) => {
            setProjects((prev) => (prev ? [project, ...prev] : [project]))
            setShowNewProject(false)
          }}
        />
      )}

      {showImportGitlab && (
        <ImportFromGitlabModal
          onClose={() => setShowImportGitlab(false)}
          onCreated={(project) => {
            setProjects((prev) => (prev ? [project, ...prev] : [project]))
            setShowImportGitlab(false)
            navigate(`/projects/${project.id}`)
          }}
        />
      )}

      {buildingProject && accessToken && (
        <BuildLogModal
          project={buildingProject}
          accessToken={accessToken}
          onClose={() => setBuildingProject(null)}
          onFinished={(patch) => patchProject(buildingProject.id, patch)}
        />
      )}

      <Dialog
        open={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        title="Delete this project?"
        description="This will stop the running container, remove source files, and delete the project permanently."
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deletingId !== null}
              onClick={() => confirmDeleteId && doDelete(confirmDeleteId)}
            >
              <Trash2 /> {deletingId ? 'Deleting…' : 'Delete project'}
            </Button>
          </>
        }
      >
        <div className="flex size-10 items-center justify-center rounded-md bg-destructive-soft text-destructive">
          <Trash2 className="size-5" />
        </div>
      </Dialog>
    </AppShell>
  )
}
